// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Scale.js
//
// Object representation of musical scales
//
//  this.mode
//  this.tonic
//  this.direction
//  this.isMajorMode (boolean)
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import { isModeName, scaleIntervals } from 'shared/definitions/musicTheory/scales';
import { scaleDegreeLongNames } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { AriaStringOptions } from './note';
import Note from './note';
import utils from '../utils';
import { toHTML } from '../uTheory.Font/font';
import KeySignature from './keySignature';

export type ModeAlias =
  | 'M'
  | 'Major'
  | 'm'
  | 'minor'
  | 'naturalMinor'
  | 'natural minor'
  | 'harmonic'
  | 'harmonic minor'
  | 'harmonicMinor'
  | 'melodic'
  | 'melodic minor'
  | 'melodicMinor'
  | 'melodicMinorAscending'
  | 'melodicMinorDescending';

export default class Scale {
  tonic: Note;

  mode: ModeName;

  isMajorMode: boolean;

  direction: 1 | -1;

  intervals: IntervalName[];

  scaleDegreeNames: string[];

  notes: Note[];

  // Public constructor
  constructor(tonic: NoteName | Note, mode: ModeAlias, direction: string | 1 | -1 = 1) {
    this.init(tonic, mode, direction);
  }

  init(tonic: NoteName | Note, mode: ModeAlias, direction: string | 1 | -1) {
    // create tonic:
    this.tonic = new Note(tonic);
    this.direction = this.processDirection(direction);

    // process name:
    const processedModeName = this.processMode(mode, this.direction);
    if (processedModeName === false) {
      throw new Error(`NaM: ${mode} is not a valid mode name.`);
    }
    this.mode = processedModeName;

    this.isMajorMode = this.mode === 'Major';

    // get intervals between scale degrees:
    this.intervals = scaleIntervals[this.mode];
    this.scaleDegreeNames = scaleDegreeLongNames[this.mode];

    // build the scale:
    this.setNotes();
  }

  setMode(mode: ModeAlias) {
    if (this.direction === -1 && mode === 'melodicMinorAscending') {
      this.direction = 1;
    }
    if (this.direction === 1 && mode === 'melodicMinorDescending') {
      this.direction = -1;
    }
    this.init(this.tonic, mode, this.direction);
    return this;
  }

  clone() {
    return new Scale(this.tonic, this.mode, this.direction);
  }

  setDirection(direction: string | 1 | -1) {
    const processedDirection = this.processDirection(direction);
    if (processedDirection === -1 && this.mode === 'melodicMinorAscending') {
      this.mode = 'melodicMinorDescending';
    }
    if (processedDirection === 1 && this.mode === 'melodicMinorDescending') {
      this.mode = 'melodicMinorAscending';
    }
    this.init(this.tonic, this.mode, processedDirection);
  }

  // eslint-disable-next-line class-methods-use-this
  processDirection(direction: string | number) {
    if (typeof direction === 'undefined') return 1;
    if (
      direction === -1 ||
      direction === '-1' ||
      direction === '-' ||
      (utils.isString(direction) && direction[0] === 'd')
    ) {
      return -1;
    }
    return 1;
  }

  processMode(mode: ModeAlias, direction: 1 | -1): ModeName | false {
    if (isModeName(mode)) return mode;
    switch (mode) {
      case 'M':
        return 'Major';
      case 'm':
        return 'naturalMinor';
      case 'minor':
      case 'natural minor':
        return 'naturalMinor';
      case 'harmonic':
      case 'harmonic minor':
        return 'harmonicMinor';
      case 'melodic':
      case 'melodic minor':
      case 'melodicMinor':
        if (this.processDirection(direction) === 1) {
          return 'melodicMinorAscending';
        }
        return 'melodicMinorDescending';
      default:
        break;
    }
    return false;
  }

  setNotes() {
    const notes: Note[] = [];
    notes[0] = this.tonic;
    for (let i = 0; i < 7; i++) {
      const degreeNote = notes[i].getNoteAt(this.intervals[i]);
      if (degreeNote === false)
        throw new Error('Invalid scale: contains double or triple accidentals.');
      notes[i + 1] = degreeNote;
    }
    if (
      this.mode === 'harmonicMinor' ||
      this.mode === 'melodicMinorAscending' ||
      this.mode === 'melodicMinorDescending'
    ) {
      notes[5].forceAccidental = true;
      notes[6].forceAccidental = true;
    }
    this.notes = notes;
  }

  /**
   * Returns "tonic" "subdominant" etc...
   * @param {number} scaleDegree 1-8
   */
  getScaleDegreeName(scaleDegree: number) {
    if (!utils.isNumber(scaleDegree)) throw new Error('Scale degree must be a number.');
    scaleDegree -= 1; // because the array is 0 based.
    scaleDegree %= 8;
    return this.scaleDegreeNames[scaleDegree];
  }

  /**
   * May return false if the note doesn't exist -- note that 7
   * when in harmonic or asc melodic returns #7 already, so
   * #7 would be DOUBLY raised, so in G# minor, F#x which doesn't
   * exist for our purposes.
   * @param scaleDegree
   */
  getNoteAtScaleDegree(scaleDegree: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8): Note;
  getNoteAtScaleDegree(scaleDegree: string): Note | false;
  getNoteAtScaleDegree(scaleDegree: number | string) {
    const accidental = `${scaleDegree}`.slice(0, 1);
    // The accidental is actually a scale degree number (between 1-8)
    if (accidental >= '1' && accidental <= '8') {
      return this.notes[Number(scaleDegree) - 1].clone();
    }
    scaleDegree = Number(`${scaleDegree}`.slice(1));
    const note = this.notes[scaleDegree - 1];
    return accidental === '#' ? note.getNoteAt('+A1') : note.getNoteAt('-A1');
  }

  modeToString(): 'Major' | 'natural minor' | 'harmonic minor' | 'melodic minor' {
    switch (this.mode) {
      case 'Major':
        return 'Major';
      case 'naturalMinor':
        return 'natural minor';
      case 'harmonicMinor':
        return 'harmonic minor';
      case 'melodicMinorAscending':
      case 'melodicMinorDescending':
        return 'melodic minor';
      default:
        throw Error('Mode must be one of Major, naturalMinor, harmonic minor, or melodic minor');
    }
  }

  /**
   * Ignores octave & direction -- as this is primarily used to prevent
   * repetitions in randomly generated scale questions.
   * @param that
   */
  equals(that: Scale) {
    // Ignores direction and octave -- as this is primarily used to prevent
    // repetitions in randomly generated scale questions
    return this.mode === that.mode && this.tonic.equalsIgnoreOctave(that.tonic);
  }

  toString(includeDirection = false) {
    let directionText = '';
    if (includeDirection) {
      directionText = this.direction === 1 ? 'ascending ' : 'descending ';
    }
    return `${directionText + this.tonic.letter + this.tonic.accidental} ${this.modeToString()}`;
  }

  toAriaString(options: AriaStringOptions = {}, includeDirection = false) {
    options = {
      letterName: true,
      accidental: true,
      octave: false,
      ...options,
    };
    const letter = options.letterName ? `${this.tonic.letter}` : '';
    const accidental = options.accidental
      ? toHTML('accidental', this.tonic.accidental, { ariaSpaceBefore: true })
      : '';
    const mode = this.modeToString();
    let directionText = '';
    if (includeDirection) {
      directionText = `${this.directionToString()} `;
    }
    return `${directionText}${letter}${accidental} ${mode}`;
  }

  public directionToString() {
    return this.direction === 1 ? 'ascending' : 'descending';
  }

  spellScale() {
    return this.notes.map((note) => note.toShortString()).join(' ');
  }

  /**
   * Given an optional array of clefs (default treble & bass) what clefs does this note
   * display in correctly?
   * @param clefsArray = ['treble', 'bass']
   * @returns {Array.<clefNames>}
   */
  prefersClef(clefsArray: ClefName[] = ['treble', 'bass']) {
    const bottomPrefers = this.notes[0].prefersClef(clefsArray);
    const topPrefers = this.notes[this.notes.length - 1].prefersClef(clefsArray);
    return utils.intersection(bottomPrefers, topPrefers);
  }

  get keySignature(): KeySignature | false {
    const quality = this.mode === 'Major' ? 'Major' : 'minor';
    try {
      const keySig = new KeySignature(this.tonic, quality);
      return keySig;
    } catch (err) {
      return false;
    }
  }

  get 1() {
    return this.getNoteAtScaleDegree(1);
  }

  get 2() {
    return this.getNoteAtScaleDegree(2);
  }

  get 3() {
    return this.getNoteAtScaleDegree(3);
  }

  get 4() {
    return this.getNoteAtScaleDegree(4);
  }

  get 5() {
    return this.getNoteAtScaleDegree(5);
  }

  get 6() {
    return this.getNoteAtScaleDegree(6);
  }

  get 7() {
    return this.getNoteAtScaleDegree(7);
  }

  get 8() {
    return this.getNoteAtScaleDegree(8);
  }
}
